<template>
    <div class="confirm-bulk-mark-as-paid-modal flex flex-col gap-10">
        <ConfirmBulkSendModalSummary :invoices="invoices" :stagesMap="stagesMap" />

        <DataTable :value="invoices" responsiveLayout="scroll" class="invoices-table" stripedRows>
            <Column field="invoice_number" :sortable="false" style="min-width: 100px">
                <template #header>
                    <div style="text-align: left; width: 100%">
                        {{ $t('§invoices.table_header_invoice_number') }}
                    </div>
                </template>
                <template #body="{ data }">
                    <span class="invoice-id">{{ data.invoice_number }}</span>
                </template>
            </Column>

            <Column field="type" :sortable="false" style="min-width: 150px">
                <template #header>
                    <div style="text-align: center; width: 100%">
                        {{ $t('§invoices.table_header_type') }}
                    </div>
                </template>
                <template #body="{ data }">
                    <div style="text-align: center">
                        <span class="invoice-type">{{ data.type }}</span>
                        <Tag
                            v-if="data.is_manual"
                            class="ml-2 manual-tag"
                            value="M"
                            severity="warning"
                        />
                    </div>
                </template>
            </Column>

            <Column field="current_stage_name" :sortable="false" style="min-width: 100px">
                <template #header>
                    <div style="text-align: center; width: 100%">
                        {{ $t('§invoices.table_header_current_stage_name') }}
                    </div>
                </template>
                <template #body="{ data }">
                    <div style="text-align: center">
                        <Tag
                            :class="getStageTagClass(data.current_stage_name)"
                            :value="data.current_stage_name"
                        />
                    </div>
                </template>
            </Column>

            <Column :sortable="false" style="width: 40px">
                <template #body>
                    <div style="text-align: center">
                        <CoreIcon name="arrow-right" size="16" />
                    </div>
                </template>
            </Column>

            <Column :sortable="false" style="min-width: 100px">
                <template #header>
                    <div style="text-align: center; width: 100%">
                        {{ $t('§invoices.table_header_next_stage') }}
                    </div>
                </template>
                <template #body>
                    <div style="text-align: center">
                        <Tag :class="getStageTagClass(paidStage)" :value="paidStage" />
                    </div>
                </template>
            </Column>

            <Column field="total_amount" :sortable="false" style="min-width: 120px">
                <template #header>
                    <div style="text-align: right; width: 100%">
                        {{ $t('§invoices.table_header_total_amount') }}
                    </div>
                </template>
                <template #body="{ data }">
                    {{ displayAsCurrency(data.total_amount, 2, data.contract_currency_code) }}
                </template>
            </Column>
        </DataTable>

        <div class="bulk-paid-total">
            <span class="bulk-paid-total__label">
                {{ $t('§invoices.modal_total_amount_to_be_paid') }}
            </span>
            <span class="bulk-paid-total__values">
                <span
                    v-for="total in totalsByCurrency"
                    :key="total.currency || 'default'"
                    class="bulk-paid-total__value"
                >
                    {{ displayAsCurrency(total.amount, 2, total.currency) }}
                </span>
            </span>
        </div>

        <div class="flex justify-start gap-2">
            <CoreButton
                :loading="invoicesStore.markingMultiple"
                :disabled="invoicesStore.markingMultiple"
                label="§ui_button.button_mark_multiple_invoices_as_paid"
                @click="handleConfirm"
                icon="pi-check-circle"
            />

            <CoreButton
                variant="tertiary"
                :disabled="invoicesStore.markingMultiple"
                label="§ui_button.cancel"
                @click="handleCancel"
                icon="pi-times"
            />
        </div>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal.js'
import { useInvoicesStore, InvoiceStageEnum } from '@/stores/invoices.js'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import CoreIcon from '@/components/core/CoreIcon.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import ConfirmBulkSendModalSummary from './ConfirmBulkSendModalSummary.vue'

export default {
    name: 'ConfirmBulkMarkAsPaidModal',

    components: {
        DataTable,
        Column,
        Tag,
        CoreIcon,
        CoreButton,
        ConfirmBulkSendModalSummary,
    },

    data() {
        return {
            modalStore: useModalStore(),
            invoicesStore: useInvoicesStore(),
            invoices: [],
            paidStage: InvoiceStageEnum.PAID,

            stagesMap: {
                created: { nextStage: 'paid' },
                dispatched: { nextStage: 'paid' },
                reminder: { nextStage: 'paid' },
                warning_1: { nextStage: 'paid' },
                warning_2: { nextStage: 'paid' },
                debt_collection: { nextStage: 'paid' },
            },
        }
    },

    computed: {
        totalsByCurrency() {
            const grouped = this.invoices.reduce((acc, invoice) => {
                const currency = invoice.contract_currency_code || null
                const amount = parseFloat(invoice.total_amount) || 0

                if (!acc[currency]) {
                    acc[currency] = { currency, amount: 0 }
                }
                acc[currency].amount += amount

                return acc
            }, {})

            return Object.values(grouped)
        },
    },

    mounted() {
        this.invoices = this.modalStore.data.invoices || []
    },

    methods: {
        getStageTagClass(stage) {
            const stageClasses = {
                created: 'stage-created',
                dispatched: 'stage-dispatched',
                reminder: 'stage-reminder',
                warning_1: 'stage-warning',
                warning_2: 'stage-warning',
                paid: 'stage-paid',
                debt_collection: 'stage-debt',
                canceled: 'stage-canceled',
            }

            return stageClasses[stage] || ''
        },

        handleCancel() {
            this.modalStore.setClose()
        },

        async handleConfirm() {
            const invoiceIDs = this.invoices.map((invoice) => invoice.id)

            await this.invoicesStore.markMultipleInvoicesAsPaid({ invoiceIDs })

            this.modalStore.setClose()
        },
    },
}
</script>

<style scoped>
.invoices-table {
    border-radius: 4px;
    overflow: hidden;
}

.invoice-id {
    color: var(--text-dark);
}

.invoice-type {
    font-size: 1.4rem;
    color: var(--text-dark);
}

.p-tag.p-component.manual-tag {
    font-size: 1.2rem;
    padding: 4px 6px;
    font-weight: 600;
    background-color: #fbc823;
}

.bulk-paid-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    border-top: 1px solid var(--color-neutral-300);
    font-size: 1.4rem;
    color: var(--text-dark);
}

.bulk-paid-total__label {
    font-weight: 400;
}

.bulk-paid-total__values {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.bulk-paid-total__value {
    font-weight: 600;
}

:deep(.p-tag) {
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 1.3rem;
    font-weight: 400;
}

:deep(.stage-created) {
    background-color: var(--color-neutral-400);
    color: var(--text-dark);
}

:deep(.stage-dispatched) {
    background-color: var(--color-primary-500);
    color: white;
}

:deep(.stage-reminder) {
    background-color: var(--color-red-300);
    color: var(--text-dark);
}

:deep(.stage-warning) {
    background-color: var(--color-red-500);
    color: white;
}

:deep(.stage-paid) {
    background-color: var(--color-primary-800);
    color: white;
}

:deep(.stage-debt) {
    background-color: var(--color-secondary-900);
    color: white;
}

:deep(.stage-canceled) {
    background-color: var(--color-neutral-700);
    color: white;
}

:deep(.p-datatable .p-datatable-tbody > tr:nth-child(odd)) {
    background-color: var(--color-neutral-100) !important;
}

:deep(.p-datatable .p-datatable-tbody > tr:nth-child(even)) {
    background-color: var(--color-neutral-400) !important;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    border: none;
}

:deep(.p-datatable) {
    color: var(--text-dark);
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
    color: var(--text-dark);
    font-weight: 500;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    color: var(--text-dark);
    font-weight: 300;
}

:deep(.p-datatable .p-datatable-thead > tr > th:last-child) {
    text-align: right !important;
}

:deep(.p-datatable .p-datatable-tbody > tr > td:last-child) {
    text-align: right !important;
}
</style>
